import { IHttpResponse, IHttpService, IPromise, IQService, IRequestShortcutConfig, IRootScopeService } from "angular";
import { ProgressColoring } from "@gtmhub/configuration/services/progress-coloring";
import { IRestLayerRequest } from "@gtmhub/core";
import { EnvironmentService } from "@gtmhub/env";
import { OkrEvents } from "@gtmhub/okrs/events";
import { PluginGtmhubAdditionalParams as GtmhubAdditionalParams } from "@gtmhub/plugins/components/plugin/plugin.component";
import { getFullCollectionInBatches } from "@gtmhub/util";
import { ICollection, ICollectionMap } from "@webapp/core/core.models";
import { IOkrClosedStatus } from "@webapp/okrs/goals/models/goal-status.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { ObjectiveFlowName } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { ITag } from "../../tags/models";
import {
  IAsigneeIdsGoalsMap,
  IBaseGoal,
  IBulkCreateGoalRequest,
  IBulkCreateGoalResult,
  IGetGoalParams,
  IGetOkrTemplateParams,
  IGoal,
  IGoalChildReorderPayload,
  IGoalChildReorderResponse,
  IGoalInTree,
  IGoalsAndMetricsDeltas,
  IOkrTemplate,
  IWorkflowActions,
} from "../models";

const CONFLICT_STATUS_CODE = 409;

const fields =
  "metrics{trend,confidence,customFields,description,ownerIds,name,attainment,insightName,fieldName,settings,lastCheckInDate,targetOperator,dynamic," +
  "format,target,critical,initialValue,actual,goalId,manualType,softDueDate,dueDate,progressStatus,links{spec},cascadeType,sourceMetricId,targetMetricIds,orderId,sessionId}," +
  "currentUserAllowedActions,parentId,parentType,ownerIds,assignee{name,email,color,avatar,picture,type},description," +
  "name,dateFrom,dateTo,childrenCount,closedStatus,fullSubTreeCount,metricsCount,designScore,attainment,attainmentTypeString,fullAggregatedAttainment,sessionId," +
  "tags{id,name,title,isActive,itemsTaggedCount},customFields,obfuscated,private,access,parentGoalSessionId," +
  "workflow{status,reviewersNeeded,reviewers,approvedAt},locked,orderId,assignees";

export const fieldsLinks = fields + ",links{spec,expanded}";

const getGoalsFieldsV2 =
  "metrics{id,name,attainment,ownerIds,manualType,goalId,customFields,actual,format,links{spec},cascadeType,sourceMetricId,targetMetricIds,orderId,confidence}," +
  "parentId,parentType,ownerIds,description," +
  "name,dateFrom,dateTo,childrenCount,fullSubTreeCount,metricsCount,attainment,attainmentTypeString,fullAggregatedAttainment,sessionId," +
  "tags{id,name,title,isActive,itemsTaggedCount},customFields,obfuscated,private,parentGoalSessionId,locked,workflow{status},orderId,designScore{totalPoints}";

interface IPatchGoalParams {
  includeMetrics?: boolean;
  gtmhubAdditionalParams?: { pluginId: string };
}

export type UpdateGoalAdditionalPayloadData = Partial<{
  flowId: string;
  flowName: ObjectiveFlowName;
}>;

export interface IResponseProcessing {
  applyColoring?: boolean;
}

export class GoalService {
  private okrEvents: OkrEvents;

  public static $inject = ["$rootScope", "$http", "$q", "EnvironmentService", "ProgressColoring"];

  private defaultResponseProcessing: IResponseProcessing = {
    applyColoring: true,
  };

  constructor(
    $rootScope: IRootScopeService,
    private $http: IHttpService,
    private $q: IQService,
    private env: EnvironmentService,
    private progressColoring: ProgressColoring
  ) {
    this.okrEvents = new OkrEvents($rootScope);
  }

  private applyColoringToGoal(goal: IGoalInTree): IGoal {
    goal.color = this.progressColoring.getColor(goal.attainment);
    goal.aggregatedAttainmentColor = this.progressColoring.getColor(goal.fullAggregatedAttainment);

    if (goal.metrics) {
      for (const metric of goal.metrics) {
        this.applyColoringToMetric(metric);
      }
    }

    return goal;
  }

  public applyColoringToGoalCollection<T>(collection: ICollection<T>): ICollection<T> {
    for (const goal of collection.items) {
      this.applyColoringToGoal(goal);
    }

    return collection;
  }

  private applyColoringToMetric(metric: Metric): Metric {
    metric.color = this.progressColoring.getColor(metric.attainment);
    return metric;
  }

  public applyColoringToMetricCollection(collection: ICollection<Metric>): ICollection<Metric> {
    for (const goal of collection.items) {
      this.applyColoringToMetric(goal);
    }

    return collection;
  }

  public getGoalsAndMetricsDeltasByOwnerIds({
    ownerIds,
    sessionIds,
    period,
    excludeOrphaned,
  }: {
    ownerIds: string[];
    sessionIds: string[];
    period: string;
    excludeOrphaned: boolean;
  }): IPromise<IGoalsAndMetricsDeltas> {
    const ownerIdsStr = ownerIds.toString();
    const url = this.env.getApiEndpoint("/delta/assignees/" + ownerIdsStr);

    const sessionIdsStr = sessionIds.toString();
    const query: IRequestShortcutConfig = {
      params: {
        sessions: sessionIdsStr,
        deltaRangeDays: period,
        excludeOrphaned,
      },
    };

    return this.$http.get<IGoalsAndMetricsDeltas>(url, query).then((response) => response.data);
  }

  public getGoalWithFields(id: string, ctrlFields = fieldsLinks): IPromise<IGoal> {
    const url = this.env.getApiEndpoint("/goals/" + id);
    const query: IRequestShortcutConfig = {
      params: {
        fields: ctrlFields || fieldsLinks,
      },
    };

    return this.$http.get<IGoal>(url, query).then((response) => this.applyColoringToGoal(response.data));
  }

  public getGoal(id: string, params?: IGetGoalParams, additionalGtmhubParams?: GtmhubAdditionalParams): IPromise<IGoal> {
    const url = this.env.getApiEndpoint("/goals/" + id);
    const config: IRequestShortcutConfig = {
      params: {
        ...params,
        ...additionalGtmhubParams,
      },
    };

    return this.$http.get<IGoal>(url, config).then((response) => this.applyColoringToGoal(response.data));
  }

  public createGoal(goal: IGoal, options: { skipNotification?: boolean; cloned?: boolean } = {}, additionalGtmhubParams?: GtmhubAdditionalParams): IPromise<IGoal> {
    const url = this.env.getApiEndpoint("/goals");
    const query: IRequestShortcutConfig = {};
    if (options.skipNotification) {
      query.params = { skipNotification: options.skipNotification };
    }
    if (options.cloned) {
      query.params = { cloned: options.cloned };
    }
    if (additionalGtmhubParams) {
      query.params = { ...query.params, ...additionalGtmhubParams };
    }

    return this.$http.post<IGoal>(url, goal, query).then((response) => {
      const newGoal = response.data;
      this.okrEvents.broadcastGoalsChanged("goalCreated", { goal: newGoal });
      return newGoal;
    });
  }

  public addTagToGoal(tag: string, goalId: string, additionalPayloadData: UpdateGoalAdditionalPayloadData = {}): IPromise<ITag> {
    const url = this.env.getApiEndpoint(`/goals/${goalId}/tags`);
    const payload = { title: tag, ...additionalPayloadData };

    return this.$http.post<ITag>(url, payload).then((response) => {
      const newTag = response.data;
      this.okrEvents.broadcastGoalsChanged("tagAdded", { goalId, tag: newTag });
      return newTag;
    });
  }

  public removeTagFromGoal(tag: ITag, goalId: string): IPromise<void> {
    const url = this.env.getApiEndpoint("/goals/" + goalId + "/tags");
    const tagCopy = angular.copy(tag);

    return this.$http({
      method: "DELETE",
      url,
      data: { title: tagCopy.title },
      headers: {
        "Content-type": "application/json;charset=utf-8",
      },
    }).then(() => this.okrEvents.broadcastGoalsChanged("tagRemoved", { goalId, tag: tagCopy }));
  }

  public patchGoal(goalId: string, patch: Partial<IGoal> & UpdateGoalAdditionalPayloadData, patchGoalParams?: IPatchGoalParams): IPromise<IGoal> {
    const url = this.env.getApiEndpoint(`/goals/${goalId}`);
    const config: IRequestShortcutConfig = {
      params: patchGoalParams,
    };

    return this.$http.patch<IGoal>(url, patch, config).then((response) => {
      const patchedGoal = response.data;
      this.okrEvents.broadcastGoalsChanged("goalPatched", { goal: patchedGoal });
      return patchedGoal;
    });
  }

  public reorderGoalChild(payload: IGoalChildReorderPayload): IPromise<IGoalChildReorderResponse> {
    const url = this.env.getApiEndpoint(`/goals/children/reorder`);
    return this.$http.patch<IGoalChildReorderResponse>(url, payload).then((response) => {
      this.okrEvents.broadcastGoalsChanged("goalChildReordered", {
        goalId: payload.parentId,
        childId: payload.moved.id,
        childType: payload.moved.type,
        childOrderId: response.data.orderId,
      });

      return response.data;
    });
  }

  public deleteGoal(goalId: string, additionalGtmhubParams?: GtmhubAdditionalParams): IPromise<void> {
    const url = this.env.getApiEndpoint(`/goals/${goalId}`);

    const config: IRequestShortcutConfig = {
      params: additionalGtmhubParams,
    };

    return this.$http.delete(url, config).then(() => this.okrEvents.broadcastGoalsChanged("goalDeleted", { goalId }));
  }

  public sendForApproval(goalId: string, reviewersIds: string[]): IPromise<void> {
    const url = this.env.getApiEndpoint(`/goals/${goalId}/review`);
    const payload = { reviewers: reviewersIds };

    return this.$http.post<void>(url, payload).then(() => this.okrEvents.broadcastGoalsChanged("reviewSentForApproval", { goalId }));
  }

  public checkUsersGoalPermissions(goalId: string, userIds: string[], permissions: string[]): IPromise<unknown> {
    const url = this.env.getApiEndpoint(`/goals/${goalId}/permissions`);
    const payload = {
      users: userIds,
      permissions: permissions,
    };

    return this.$http.post<unknown>(url, payload).then(
      (response) => response.data,
      (response: IHttpResponse<unknown>) => {
        if (response.status && response.status === CONFLICT_STATUS_CODE) {
          return response.data;
        } else {
          return this.$q.reject(response);
        }
      }
    );
  }

  public getGoalWorkflowActions(goalId: string): IPromise<IWorkflowActions> {
    const url = this.env.getApiEndpoint(`/goals/${goalId}/workflow-actions`);

    return this.$http.get<IWorkflowActions>(url).then((response) => response.data);
  }

  public approveGoal(goalId: string, comment?): IPromise<void> {
    const url = this.env.getApiEndpoint(`/goals/${goalId}/approve`);
    const payload = { comment };

    return this.$http.post<void>(url, payload).then(() => this.okrEvents.broadcastGoalsChanged("reviewApproved", { goalId }));
  }

  public declineGoal(goalId: string, comment?: string): IPromise<void> {
    const url = this.env.getApiEndpoint(`/goals/${goalId}/decline`);
    const payload = { comment };

    return this.$http.post<void>(url, payload).then(() => this.okrEvents.broadcastGoalsChanged("reviewDeclined", { goalId }));
  }

  public withdrawGoalFromReview(goalId: string): IPromise<void> {
    const url = this.env.getApiEndpoint(`/goals/${goalId}/withdraw`);

    return this.$http.post<void>(url, null).then(() => this.okrEvents.broadcastGoalsChanged("reviewWithdrawn", { goalId }));
  }

  public getGoalsGroupedByOwners(sessionId: string, skip = null, take = null, approvalStatus = null): IPromise<ICollectionMap<IAsigneeIdsGoalsMap>> {
    const url = this.env.getApiEndpoint("/goals/groupBy/owners");
    const query: IRequestShortcutConfig = {
      params: {
        sessionId: sessionId,
        skip: skip,
        take: take,
        approvalStatus: approvalStatus,
      },
    };

    return this.$http.get<ICollectionMap<IAsigneeIdsGoalsMap>>(url, query).then((response) => response.data);
  }

  public getGoalsGroupedByReviewers(sessionId: string, skip = null, take = null, review = null, approvalStatus = null): IPromise<ICollectionMap<IAsigneeIdsGoalsMap>> {
    const url = this.env.getApiEndpoint("/goals/groupBy/reviewers");
    const query: IRequestShortcutConfig = {
      params: {
        sessionId: sessionId,
        skip: skip,
        take: take,
        review: review,
        approvalStatus: approvalStatus,
      },
    };

    return this.$http.get<ICollectionMap<IAsigneeIdsGoalsMap>>(url, query).then((response) => response.data);
  }

  public submitToMarketplaceV2(goalId: string, data: { includeSubmitter: boolean }): IPromise<void> {
    const url = this.env.getMarketplaceEndpointV2(`/submit-to-marketplace/okr/${goalId}`);

    return this.$http.post<void>(url, data).then((response) => response.data);
  }

  public getGoalTemplatesFromMarketplace(params: IGetOkrTemplateParams): IPromise<ICollection<IOkrTemplate>> {
    const url = this.env.getMarketplaceEndpointV2(`/templates/goals`);

    return this.$http.get<ICollection<IOkrTemplate>>(url, { params }).then((response) => response.data);
  }

  public getAllGoalsV2<T>(params: IRestLayerRequest): IPromise<ICollection<T>> {
    if (!params.fields) {
      params.fields = getGoalsFieldsV2;
    }

    const url = this.env.getApiEndpointV2("/goals");
    return getFullCollectionInBatches<T>(url, { params }, this.$http, this.$q).then((collection) => this.applyColoringToGoalCollection(collection));
  }

  public getGoalsV2<T extends IBaseGoal>(
    params: IRestLayerRequest,
    additionalGtmhubParams?: GtmhubAdditionalParams,
    responseProcessing: IResponseProcessing = this.defaultResponseProcessing
  ): IPromise<ICollection<T>> {
    if (!params.fields) {
      params.fields = getGoalsFieldsV2;
    }

    const url = this.env.getApiEndpointV2("/goals");

    const config: IRequestShortcutConfig = {
      params: { ...params, ...additionalGtmhubParams },
    };

    return this.$http.get<ICollection<T>>(url, config).then((response) => {
      if (responseProcessing.applyColoring) {
        return this.applyColoringToGoalCollection(response.data);
      }

      return response.data;
    });
  }

  public createBulkGoals(okrs: IBulkCreateGoalRequest[]): IPromise<IBulkCreateGoalResult[]> {
    const url: string = this.env.getApiEndpoint("/goals/bulk");
    return this.$http.post<IBulkCreateGoalResult[]>(url, okrs).then((response) => response.data);
  }

  public closeGoal(id: string, closedStatus: IOkrClosedStatus): IPromise<void> {
    const url = this.env.getApiEndpoint(`/goals/${id}/status`);

    return this.$http.put<void>(url, closedStatus).then((response) => response.data);
  }

  public reopenGoal(id: string): IPromise<void> {
    const url = this.env.getApiEndpoint(`/goals/${id}/status`);

    return this.$http.delete<void>(url).then((response) => response.data);
  }
}
